import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Car, Wrench, CheckCheck, PackageSearch, Users, Clock, DollarSign, ChevronRight } from "lucide-react";

const STATUS_LABEL = {
  entrou: "Veículo Entrou",
  em_inspecao: "Em Inspeção",
  aguardando_estimate: "Aguardando Estimate",
  aguardando_aprovacao: "Aguardando Aprovação",
  aguardando_pecas: "Aguardando Peças",
  em_andamento: "Em Andamento",
  pronto: "Pronto",
  entregue: "Entregue",
};

const STATUS_COLOR = {
  entrou: "bg-slate-500/15 text-slate-500",
  em_inspecao: "bg-amber-500/15 text-amber-500",
  aguardando_estimate: "bg-secondary text-secondary-foreground",
  aguardando_aprovacao: "bg-orange-500/15 text-orange-500 animate-pulse",
  aguardando_pecas: "bg-purple-500/15 text-purple-500",
  em_andamento: "bg-primary/15 text-primary",
  pronto: "bg-emerald-500/15 text-emerald-500",
  entregue: "bg-neutral-500/15 text-neutral-500",
};

function Kpi({ icon: Icon, label, value, tone = "" }) {
  return (
    <div className="border border-border rounded-md bg-card p-3 md:p-4 hover:-translate-y-0.5 transition-transform" data-testid={`kpi-${label}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-muted-foreground truncate">{label}</div>
          <div className={`font-display text-2xl md:text-3xl font-bold mt-1 md:mt-2 tracking-tight ${tone}`}>{value ?? "—"}</div>
        </div>
        <div className="w-7 h-7 md:w-8 md:h-8 rounded-md bg-secondary flex items-center justify-center text-muted-foreground shrink-0">
          <Icon className="w-3.5 h-3.5 md:w-4 md:h-4" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, j] = await Promise.all([api.get("/dashboard/stats"), api.get("/jobs")]);
        setStats(s.data);
        setJobs(j.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Visão geral</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Dashboard Operacional</h1>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Kpi icon={Car} label="Na Oficina" value={stats?.veiculos_na_oficina} />
        <Kpi icon={Wrench} label="Em Andamento" value={stats?.em_andamento} tone="text-primary" />
        <Kpi icon={Clock} label="Aguard. Aprovação" value={stats?.aguardando_aprovacao} tone="text-orange-500" />
        <Kpi icon={PackageSearch} label="Aguard. Peças" value={stats?.aguardando_pecas} />
        <Kpi icon={CheckCheck} label="Prontos" value={stats?.prontos_hoje} tone="text-emerald-500" />
        <Kpi icon={Users} label="Clientes" value={stats?.total_clientes} />
        <Kpi icon={DollarSign} label="Veículos" value={stats?.total_veiculos} />
      </div>

      <div className="flex items-center justify-between mt-8">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">Veículos na oficina</h2>
          <p className="text-sm text-muted-foreground">Cards ativos por ordem de serviço.</p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : jobs.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground text-sm">
          Nenhum job ativo no momento.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="job-cards-grid">
          {jobs.map((j) => (
            <button
              key={j.id}
              onClick={() => navigate("/kanban")}
              className="group text-left border border-border rounded-md bg-card overflow-hidden hover:-translate-y-0.5 hover:shadow-sm transition-all"
              data-testid={`job-card-${j.id}`}
            >
              <div className="h-32 bg-secondary relative overflow-hidden">
                {j.vehicle?.photo_url ? (
                  <img src={j.vehicle.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <Car className="w-8 h-8" />
                  </div>
                )}
                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium ${STATUS_COLOR[j.status]}`}>
                  {STATUS_LABEL[j.status]}
                </div>
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] font-mono">
                  {j.days_in_shop}d
                </div>
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold text-sm tracking-tight truncate">
                    {j.vehicle?.year} {j.vehicle?.make} {j.vehicle?.model}
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">{j.client_name}</div>
                <div className="flex items-center justify-between mt-3 text-[11px]">
                  <div className="text-muted-foreground truncate">{j.service_type}</div>
                  <div className="font-mono text-muted-foreground">{j.technician || "—"}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
